import { AddRounded, CheckCircleOutline } from "@mui/icons-material";
import { IconButton, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { Accordion, AccordionSkeleton, CreateTaskItemModal, TaskItem, ViewList } from "..";
import { useGetIssueTaskItems, type IssueDto, type TaskItemDto } from "../../http";

interface IProps {
  issueGuid: string;
  issue?: IssueDto;
  openDrawerOnTaskItemClick?: boolean;
}

const IssueTaskList = (props: IProps) => {
  const { data, isLoading } = useGetIssueTaskItems(props.issueGuid);
  const hasTaskItems = (data?.taskItems.length ?? 0) > 0;
  const [isExpanded, setIsExpanded] = useState<boolean | undefined>(undefined);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const isAccordionExpanded = isExpanded ?? hasTaskItems;

  function handleAddClicked() {
    setIsCreateModalVisible(true);
  }

  return isLoading || !data ? (
    <AccordionSkeleton />
  ) : (
    <>
      <Accordion
        title={(expanded) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CheckCircleOutline sx={{ color: "text.secondary" }} />
            <Stack direction="row" spacing={0.25}>
              <Typography variant="label">Tasks</Typography>
              <Typography variant="caption">{expanded ? undefined : `(${data.taskItems.length})`}</Typography>
            </Stack>
          </Stack>
        )}
        expanded={isAccordionExpanded}
        onExpandClicked={() => {
          setIsExpanded(!isAccordionExpanded);
        }}
        hoverActions={
          <IconButton size="small" onClick={handleAddClicked}>
            <AddRounded />
          </IconButton>
        }
        hoverActionsAlwaysVisible
        accordionDetailsSx={{ px: 0.5, pb: 0.5 }}
      >
        <ViewList
          dataSource={data.taskItems}
          isLoading={isLoading}
          spacing={0.5}
          renderItem={(taskItem: TaskItemDto) => (
            <TaskItem
              key={taskItem.guid}
              taskItem={taskItem}
              showTeamChip
              userCanEdit={taskItem.userCanEdit}
              sx={{ px: 0.5 }}
              openDrawerOnClick={props.openDrawerOnTaskItemClick}
            />
          )}
          placeholder={"No tasks yet."}
        />
      </Accordion>

      {props.issueGuid && (
        <>
          <CreateTaskItemModal
            visible={isCreateModalVisible}
            parentIssue={{ issueGuid: props.issueGuid, description: props.issue?.description ?? "" }}
            defaultSelectedTeamSlug={props.issue?.assignedTeam?.slug}
            onCreated={() => {
              setIsCreateModalVisible(false);
              setIsExpanded(true);
            }}
            onCancelled={() => setIsCreateModalVisible(false)}
          />
        </>
      )}
    </>
  );
};

export { IssueTaskList };
